import { useEffect, useMemo, useState } from 'react';
import { i18n } from '@/shared/i18n';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ToggleGroup,
  ToggleGroupItem,
} from '@monksflow/monks-ui';
import { ArrowRight, Minus, Pencil, Plus, SlidersHorizontal } from 'lucide-react';
import {
  diffVersions,
  type FieldDiff,
  type PropChange,
  type RuleDiff,
  type TaxonomyVersion,
  type VersionDiff,
} from '@/entities/taxonomy';
import { FormDialog } from '@/shared/ui/FormDialog';
import { SearchInput } from '@/shared/ui/SearchInput';

// eslint-disable-next-line i18next/no-literal-string -- sentinel for the empty baseline
const EMPTY = '__empty__';

type Filter = 'all' | 'added' | 'removed' | 'modified';

export interface VersionDiffModalProps {
  open: boolean;
  onClose: () => void;
  versions: TaxonomyVersion[];
  /** Baseline version id (null = compare against an empty baseline). */
  initialFromId: string | null;
  /** The version whose changes we're viewing. */
  initialToId: string;
  /** Show From/To selectors so any two versions can be compared. */
  allowPicker?: boolean;
}

function versionLabel(v: TaxonomyVersion): string {
  const status = v.status.charAt(0).toUpperCase() + v.status.slice(1);
  return `v${v.versionNumber} · ${status}`;
}

const TONE = {
  add: {
    stripe: 'border-l-emerald-500',
    icon: 'text-emerald-600 dark:text-emerald-400',
    chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
    Icon: Plus,
  },
  remove: {
    stripe: 'border-l-destructive',
    icon: 'text-destructive',
    chip: 'bg-destructive/10 text-destructive',
    Icon: Minus,
  },
  modify: {
    stripe: 'border-l-amber-500',
    icon: 'text-amber-600 dark:text-amber-400',
    chip: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
    Icon: Pencil,
  },
} as const;

/** Compact "+3 −1 ~2" tally used on History entries. */
export function DiffCountBadge({ diff }: { diff: VersionDiff }) {
  const { t } = useTranslation('knowledge');
  const { added, removed, modified } = diff.counts;
  if (diff.isEmpty)
    return (
      <span className="text-xs text-muted-foreground">
        {t('taxonomy.versionDiffModal.noChanges')}
      </span>
    );
  /**
   * The tallies count FIELDS only, while `totalChanges` covers rules and version
   * properties too — so a version whose only edit was a delimiter, an output
   * formula or the tracking-code pattern rendered an empty badge beside a "See
   * changes" link. Rare when only description/markets/platforms were compared;
   * routine now that settings and outputs are.
   *
   * Derived by subtraction rather than gated on "no field changes at all", which
   * was the first attempt and undercounted the other way: a version that added a
   * field AND rewrote three rules showed a bare `+1`.
   */
  const fieldTallies = added + removed + modified;
  const other = diff.totalChanges - fieldTallies;
  return (
    <span className="flex items-center gap-1.5 text-xs font-medium">
      {added > 0 && <span className="text-emerald-600 dark:text-emerald-400">+{added}</span>}
      {removed > 0 && <span className="text-destructive">−{removed}</span>}
      {modified > 0 && <span className="text-amber-600 dark:text-amber-400">~{modified}</span>}
      {other > 0 && (
        <span className="text-amber-600 dark:text-amber-400">
          {t('taxonomy.versionDiffModal.otherChanges', { count: other })}
        </span>
      )}
    </span>
  );
}

/** A tinted summary pill in the control bar (e.g. "24 added"). */
function SummaryChip({
  tone,
  count,
  label,
}: {
  tone: keyof typeof TONE;
  count: number;
  label: string;
}) {
  if (count === 0) return null;
  const t = TONE[tone];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${t.chip}`}
    >
      <t.Icon className="size-3" aria-hidden />
      {count} {label}
    </span>
  );
}

function GroupChip({ group }: { group: string }) {
  return (
    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      {group}
    </span>
  );
}

function FieldRow({ field, tone }: { field: FieldDiff; tone: keyof typeof TONE }) {
  const { t } = useTranslation('knowledge');
  const styles = TONE[tone];
  return (
    <div
      className={`border-b border-l-2 border-border-light ${styles.stripe} py-2 pl-3 pr-2 transition-colors hover:bg-muted/40`}
    >
      <div className="flex items-center gap-2">
        <styles.Icon className={`size-3.5 shrink-0 ${styles.icon}`} aria-hidden />
        <span
          className={`text-sm font-medium ${tone === 'remove' ? 'text-muted-foreground line-through' : 'text-foreground'}`}
        >
          {field.name}
        </span>
        <GroupChip group={field.group} />
      </div>

      {field.attrChanges.length > 0 && (
        <ul className="mt-1 space-y-0.5 pl-5">
          {field.attrChanges.map((c) => (
            <li
              key={c.label}
              className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
            >
              <span className="font-medium text-foreground/80">{c.label}</span>
              <span className="rounded bg-muted px-1 py-0.5 line-through">{c.old}</span>
              <ArrowRight className="size-3" aria-hidden />
              <span className="rounded bg-emerald-50 px-1 py-0.5 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                {c.new}
              </span>
            </li>
          ))}
        </ul>
      )}

      {field.valueChanges.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1 pl-5">
          {field.valueChanges.map((v, i) => (
            <span
              key={`${v.value}-${i}`}
              className={`rounded px-1.5 py-0.5 text-[11px] ${
                v.kind === 'added'
                  ? TONE.add.chip
                  : v.kind === 'removed'
                    ? TONE.remove.chip
                    : TONE.modify.chip
              }`}
            >
              {v.kind === 'added' &&
                `+ ${v.value || t('taxonomy.versionDiffModal.blank')} (${v.code || '—'})`}
              {v.kind === 'removed' &&
                `− ${v.value || t('taxonomy.versionDiffModal.blank')} (${v.code || '—'})`}
              {v.kind === 'code_changed' &&
                `${v.value || t('taxonomy.versionDiffModal.blank')}: ${v.oldCode || t('taxonomy.versionDiffModal.none')} → ${v.newCode || t('taxonomy.versionDiffModal.none')}`}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function RuleRow({ rule, tone }: { rule: RuleDiff; tone: keyof typeof TONE }) {
  const t = TONE[tone];
  return (
    <div
      className={`border-b border-l-2 border-border-light ${t.stripe} py-2 pl-3 pr-2 transition-colors hover:bg-muted/40`}
    >
      <div className="flex items-start gap-2">
        <t.Icon className={`mt-0.5 size-3.5 shrink-0 ${t.icon}`} aria-hidden />
        <div className="min-w-0">
          <p
            className={`text-xs ${tone === 'remove' ? 'text-muted-foreground line-through' : 'text-foreground'}`}
          >
            {rule.summary}
          </p>
          {rule.changes.map((c, i) => (
            <p key={i} className="mt-0.5 text-xs text-muted-foreground">
              {c}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

function PropRow({ change }: { change: PropChange }) {
  return (
    <div className="border-b border-l-2 border-l-amber-500 border-border-light py-2 pl-3 pr-2">
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="font-medium text-foreground">{change.label}</span>
        <span className="rounded bg-muted px-1 py-0.5 text-muted-foreground line-through">
          {change.old}
        </span>
        <ArrowRight className="size-3 text-muted-foreground" aria-hidden />
        <span className="rounded bg-emerald-50 px-1 py-0.5 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
          {change.new}
        </span>
      </div>
    </div>
  );
}

function SectionHeader({ title, count }: { title: string; count: number }) {
  return (
    <div className="bg-card pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {title} <span className="text-muted-foreground/60">({count})</span>
    </div>
  );
}

/** One entry in the flattened diff list. */
type DiffListRow =
  | { kind: 'header'; key: string; title: string; count: number }
  | { kind: 'field'; key: string; field: FieldDiff; tone: keyof typeof TONE }
  | { kind: 'rule'; key: string; rule: RuleDiff; tone: keyof typeof TONE }
  | { kind: 'prop'; key: string; change: PropChange };

/**
 * Compares two taxonomy version snapshots. Built to stay readable AND fast at
 * hundreds of changes: a fixed control bar (version pills/selectors, summary
 * chips, a change-type filter and a name search) sits above a scrolling body of
 * color-striped rows flattened across sections. The body is deliberately NOT
 * windowed — see the note on it for why, and why that is affordable here.
 * The diff is computed client-side from snapshots already loaded — no backend.
 */
export function VersionDiffModal(props: VersionDiffModalProps) {
  const { t } = useTranslation('knowledge');
  const { open, onClose, versions, initialFromId, initialToId, allowPicker } = props;
  const [fromId, setFromId] = useState<string>(initialFromId ?? EMPTY);
  const [toId, setToId] = useState<string>(initialToId);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) {
      setFromId(initialFromId ?? EMPTY);
      setToId(initialToId);
      setFilter('all');
      setSearch('');
    }
  }, [open, initialFromId, initialToId]);

  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => b.versionNumber - a.versionNumber),
    [versions],
  );

  const fromVersion = fromId === EMPTY ? null : (versions.find((v) => v.id === fromId) ?? null);
  const toVersion = versions.find((v) => v.id === toId);
  const diff = useMemo(
    () => (toVersion ? diffVersions(fromVersion, toVersion) : null),
    [fromVersion, toVersion],
  );

  // Apply the change-type filter + name search to each bucket.
  const view = useMemo(() => {
    if (!diff) return null;
    const q = search.trim().toLowerCase();
    const fMatch = (f: FieldDiff) => !q || f.name.toLowerCase().includes(q);
    const rMatch = (r: RuleDiff) => !q || r.summary.toLowerCase().includes(q);
    const show = (f: Filter) => filter === 'all' || filter === f;
    return {
      fieldsAdded: show('added') ? diff.fieldsAdded.filter(fMatch) : [],
      fieldsRemoved: show('removed') ? diff.fieldsRemoved.filter(fMatch) : [],
      fieldsModified: show('modified') ? diff.fieldsModified.filter(fMatch) : [],
      rulesAdded: show('added') ? diff.rulesAdded.filter(rMatch) : [],
      rulesRemoved: show('removed') ? diff.rulesRemoved.filter(rMatch) : [],
      rulesModified: show('modified') ? diff.rulesModified.filter(rMatch) : [],
      propChanges: show('modified') && !q ? diff.propChanges : [],
    };
  }, [diff, filter, search]);

  // Flatten the visible buckets into one list with section headers interleaved,
  // so the body is a single pass of uniform rows rather than nested maps per
  // section.
  const listRows = useMemo<DiffListRow[]>(() => {
    if (!view) return [];
    const rows: DiffListRow[] = [];
    const pushFields = (title: string, list: FieldDiff[], tone: keyof typeof TONE) => {
      if (list.length === 0) return;
      rows.push({ kind: 'header', key: `h-${title}`, title, count: list.length });
      for (const f of list) rows.push({ kind: 'field', key: `f-${f.id}`, field: f, tone });
    };
    const pushRules = (title: string, list: RuleDiff[], tone: keyof typeof TONE) => {
      if (list.length === 0) return;
      rows.push({ kind: 'header', key: `h-${title}`, title, count: list.length });
      for (const r of list) rows.push({ kind: 'rule', key: `r-${r.id}`, rule: r, tone });
    };
    pushFields(t('taxonomy.versionDiffModal.fieldsAdded'), view.fieldsAdded, 'add');
    pushFields(t('taxonomy.versionDiffModal.fieldsModified'), view.fieldsModified, 'modify');
    pushFields(t('taxonomy.versionDiffModal.fieldsRemoved'), view.fieldsRemoved, 'remove');
    pushRules(t('taxonomy.versionDiffModal.rulesAdded'), view.rulesAdded, 'add');
    pushRules(t('taxonomy.versionDiffModal.rulesModified'), view.rulesModified, 'modify');
    pushRules(t('taxonomy.versionDiffModal.rulesRemoved'), view.rulesRemoved, 'remove');
    if (view.propChanges.length > 0) {
      rows.push({
        kind: 'header',
        key: 'h-props',
        title: t('taxonomy.versionDiffModal.properties'),
        count: view.propChanges.length,
      });
      for (const c of view.propChanges) rows.push({ kind: 'prop', key: `p-${c.label}`, change: c });
    }
    return rows;
  }, [view, t]);

  const visibleCount = listRows.filter((r) => r.kind !== 'header').length;

  const renderRow = (row: DiffListRow) => {
    switch (row.kind) {
      case 'header':
        return <SectionHeader key={row.key} title={row.title} count={row.count} />;
      case 'field':
        return <FieldRow key={row.key} field={row.field} tone={row.tone} />;
      case 'rule':
        return <RuleRow key={row.key} rule={row.rule} tone={row.tone} />;
      case 'prop':
        return <PropRow key={row.key} change={row.change} />;
    }
  };

  const renderVersionControl = () => {
    if (allowPicker) {
      return (
        <div className="flex items-center gap-2">
          <Select value={fromId} onValueChange={setFromId}>
            <SelectTrigger className="h-8 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={EMPTY}>
                {i18n.t('knowledge:taxonomy.versionDiffModal.initialEmpty')}
              </SelectItem>
              {sortedVersions.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {versionLabel(v)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ArrowRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <Select value={toId} onValueChange={setToId}>
            <SelectTrigger className="h-8 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sortedVersions.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {versionLabel(v)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="rounded-md border border-border-light px-2 py-1 text-muted-foreground">
          {fromVersion
            ? `v${fromVersion.versionNumber}`
            : i18n.t('knowledge:taxonomy.versionDiffModal.initial')}
        </span>
        <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
        <span className="rounded-md border border-border-light bg-muted px-2 py-1 font-medium text-foreground">
          {i18n.t('knowledge:taxonomy.versionDiffModal.v')}
          {toVersion?.versionNumber}
        </span>
      </div>
    );
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={t('taxonomy.versionDiffModal.compareVersions')}
      className="flex h-[86dvh] max-h-[86dvh] w-[min(calc(100vw-2rem),72rem)] flex-col sm:max-w-[72rem]"
    >
      <FormDialog.Body className="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden">
        {/* Control bar (fixed) */}
        <div className="shrink-0 space-y-3 border-b border-border-light px-5 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {renderVersionControl()}
            <span className="text-sm text-muted-foreground">
              {diff && !diff.isEmpty
                ? t('taxonomy.versionDiffModal.changeCount', { count: diff.totalChanges })
                : t('taxonomy.versionDiffModal.noChanges')}
            </span>
          </div>

          {diff && !diff.isEmpty && (
            <>
              <div className="flex flex-wrap items-center gap-1.5">
                <SummaryChip
                  tone="add"
                  count={diff.counts.added}
                  label={t('taxonomy.versionDiffModal.added')}
                />
                <SummaryChip
                  tone="remove"
                  count={diff.counts.removed}
                  label={t('taxonomy.versionDiffModal.removed')}
                />
                <SummaryChip
                  tone="modify"
                  count={diff.counts.modified}
                  label={t('taxonomy.versionDiffModal.modified')}
                />
                {diff.rulesAdded.length + diff.rulesRemoved.length + diff.rulesModified.length >
                  0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
                    {diff.rulesAdded.length + diff.rulesRemoved.length + diff.rulesModified.length}{' '}
                    {t('taxonomy.versionDiffModal.rules')}
                  </span>
                )}
                {diff.propChanges.length > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {diff.propChanges.length} {t('taxonomy.versionDiffModal.properties2')}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <ToggleGroup
                  aria-label={t('taxonomy.versionDiffModal.showChangesGroup')}
                  type="single"
                  value={filter}
                  onValueChange={(v) => v && setFilter(v as Filter)}
                  variant="outline"
                  size="sm"
                >
                  {/* eslint-disable i18next/no-literal-string -- `value` is the filter key */}
                  <ToggleGroupItem value="all">
                    {t('taxonomy.versionDiffModal.all')}
                  </ToggleGroupItem>
                  <ToggleGroupItem value="added">
                    {t('taxonomy.versionDiffModal.added2')}
                  </ToggleGroupItem>
                  <ToggleGroupItem value="removed">
                    {t('taxonomy.versionDiffModal.removed2')}
                  </ToggleGroupItem>
                  <ToggleGroupItem value="modified">
                    {t('taxonomy.versionDiffModal.modified2')}
                  </ToggleGroupItem>
                  {/* eslint-enable i18next/no-literal-string */}
                </ToggleGroup>
                <SearchInput
                  size="sm"
                  value={search}
                  onChange={setSearch}
                  placeholder={t('taxonomy.versionDiffModal.searchFields')}
                  aria-label={t('taxonomy.versionDiffModal.searchFieldsLabel')}
                  className="w-56"
                />
              </div>
            </>
          )}
        </div>

        {/* Scrolling diff body. Rows are cheap static markup, so a plain render
            stays smooth even at hundreds of changes; react-virtual doesn't
            measure reliably inside the Radix dialog portal, so we don't window
            here (the heavy virtualization is on the editable table). */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">
          {!diff ? (
            <p className="py-16 text-center text-sm text-muted-foreground">
              {t('taxonomy.versionDiffModal.selectTwoVersionsCompare')}
            </p>
          ) : diff.isEmpty ? (
            <p className="py-16 text-center text-sm text-muted-foreground">
              {t('taxonomy.versionDiffModal.noDifferencesBetweenTheseVersions')}
            </p>
          ) : visibleCount === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-center">
              <SlidersHorizontal className="size-5 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">
                {t('taxonomy.versionDiffModal.noChangesMatchFilter')}
              </p>
            </div>
          ) : (
            <div className="flex flex-col">{listRows.map(renderRow)}</div>
          )}
        </div>
      </FormDialog.Body>

      <FormDialog.Footer
        primary={{ label: t('taxonomy.versionDiffModal.done'), onClick: onClose }}
      />
    </FormDialog>
  );
}

VersionDiffModal.displayName = 'VersionDiffModal';
